import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownItemContent,
  IconButton,
  Tag,
} from '@ads/components-react';
import {
  AcademicCapIcon,
  ClockIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  EyeIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';

import type {
  DocumentationStatus,
  EnrollmentScholarshipFull,
  ScholarshipActiveStatus,
  ScholarshipDetail,
  ScholarshipDocumentItem,
  ScholarshipEnrollmentStatus,
} from '../../../types/enrollment';

// ─── Props ────────────────────────────────────────────────────────────────────

/**
 * Tab de bolsa da gestão de matrícula.
 *
 * Quando `scholarshipFull` está disponível, exibe a visão detalhada completa
 * (configuração, documentos e histórico). Caso contrário, exibe a lista resumida
 * de bolsas a partir de `scholarships`.
 *
 * @param scholarships    - Lista resumida de bolsas (endpoint scholarship-tab)
 * @param scholarshipFull - Dados completos da bolsa (endpoint futuro)
 * @param onAlterStatus   - Callback para abrir o painel de alteração de status
 * @param onExtendDeadline - Callback para prorrogar o prazo de documentos
 */
export interface EnrollmentScholarshipTabProps {
  readonly scholarships: readonly ScholarshipDetail[];
  readonly scholarshipFull?: EnrollmentScholarshipFull | null;
  readonly onAlterStatus?: () => void;
  readonly onExtendDeadline?: () => void;
}

// ─── Local sub-components (mesmos das outras tabs) ────────────────────────────

function SectionCard({
  children,
  header,
}: {
  children: React.ReactNode;
  header?: React.ReactNode;
}) {
  return (
    <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface overflow-hidden">
      {header && (
        <div className="border-b-hairline border-ctx-layout-border px-xxs py-quark">{header}</div>
      )}
      <div className="px-xxs py-xxxs">{children}</div>
    </div>
  );
}

function SectionTitle({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between py-nano">
      <div className="flex items-center gap-mini">
        {Icon && <Icon className="h-xxxs w-xxxs text-ctx-content-placeholder" />}
        <div className="flex flex-col">
          <span className="ts-paragraph-xs !font-medium text-ctx-content-title">{title}</span>
          {description && (
            <span className="ts-caption-xxs text-ctx-content-placeholder">{description}</span>
          )}
        </div>
      </div>
      {action}
    </div>
  );
}

function InfoField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-quark">
      <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
        {label}
      </span>
      <span className="ts-paragraph-xs !font-medium text-ctx-content-title">{value ?? '—'}</span>
    </div>
  );
}

// ─── Helpers & constants ──────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

const ACTIVE_STATUS_TAG_MAP: Record<
  ScholarshipActiveStatus,
  'positive' | 'helper' | 'negative' | 'default'
> = {
  active: 'positive',
  suspended: 'helper',
  cancelled: 'negative',
  pending_docs: 'helper',
};

const ACTIVE_STATUS_DOT_MAP: Record<ScholarshipActiveStatus, string> = {
  active: 'border-base-success-500',
  suspended: 'border-base-warning-500',
  cancelled: 'border-base-danger-500',
  pending_docs: 'border-base-neutral-400',
};

const SCHOLARSHIP_ENROLLMENT_STATUS_TAG_MAP: Record<
  ScholarshipEnrollmentStatus,
  'positive' | 'helper' | 'negative' | 'default'
> = {
  interested: 'helper',
  in_process: 'helper',
  scholarship_holder: 'positive',
  refused: 'negative',
  refused_interested: 'negative',
  refused_potential: 'negative',
};

const DOC_STATUS_TAG_MAP: Record<
  DocumentationStatus,
  'positive' | 'helper' | 'negative' | 'default'
> = {
  approved: 'positive',
  approved_temporary: 'helper',
  pending: 'helper',
  rejected: 'negative',
  not_sent: 'default',
};

// ─── Scholarship document row (full view) ────────────────────────────────────

function ScholarshipDocumentRow({ doc }: { doc: ScholarshipDocumentItem }) {
  const { t } = useTranslation('personManagement');

  const buildSubtitle = (): string => {
    switch (doc.status) {
      case 'approved':
        if (doc.approvedAt && doc.approvedBy) {
          return t('enrollmentDocumentation.approvedAtBy', {
            date: new Date(doc.approvedAt).toLocaleDateString('pt-BR'),
            by: doc.approvedBy,
          });
        }
        return '';
      case 'approved_temporary':
        if (doc.uploadedAt) {
          return t('enrollmentDocumentation.approvedTempAt', {
            date: new Date(doc.uploadedAt).toLocaleDateString('pt-BR'),
          });
        }
        return '';
      case 'pending':
        if (doc.uploadedAt) {
          return t('enrollmentDocumentation.pendingAt', {
            date: new Date(doc.uploadedAt).toLocaleDateString('pt-BR'),
          });
        }
        return '';
      case 'rejected':
        if (doc.uploadedAt) {
          return t('enrollmentDocumentation.rejectedAt', {
            date: new Date(doc.uploadedAt).toLocaleDateString('pt-BR'),
          });
        }
        return '';
      case 'not_sent':
        return t('enrollmentDocumentation.awaitingUpload');
    }
  };

  const isNotSent = doc.status === 'not_sent';

  return (
    <div className="flex items-center gap-xxs py-nano">
      <div className="flex h-[1.75rem] w-[1.75rem] shrink-0 items-center justify-center rounded-sm bg-ctx-layout-body">
        <DocumentTextIcon className="h-xxxs w-xxxs text-ctx-content-placeholder" />
      </div>
      <div className="flex min-w-none flex-1 flex-col gap-quark">
        <span className="ts-paragraph-xs !font-medium text-ctx-content-title line-clamp-1">
          {doc.type}
        </span>
        <span className="ts-caption-xxs text-ctx-content-placeholder">{buildSubtitle()}</span>
      </div>
      <div className="flex shrink-0 items-center gap-quark">
        <Tag
          label={t(`documentationStatus.${doc.status}`)}
          size="sm"
          status={DOC_STATUS_TAG_MAP[doc.status]}
          custom={{ text: 'text-nowrap' }}
        />
        {isNotSent ? (
          <Button hierarchy="tertiary" size="sm" leadingIcon={PaperClipIcon}>
            {t('enrollmentDocumentation.attach')}
          </Button>
        ) : (
          <>
            <IconButton
              icon={EyeIcon}
              hierarchy="tertiary"
              size="sm"
              ariaLabel={t('actions.view')}
            />
            <Dropdown
              trigger={
                <IconButton
                  icon={EllipsisHorizontalIcon}
                  hierarchy="tertiary"
                  size="sm"
                  ariaLabel={t('actions.moreOptions')}
                />
              }
              align="end"
            >
              <DropdownItem onSelect={() => {}}>
                <DropdownItemContent>{t('actions.approve')}</DropdownItemContent>
              </DropdownItem>
              <DropdownItem onSelect={() => {}}>
                <DropdownItemContent>{t('actions.approveTemporary')}</DropdownItemContent>
              </DropdownItem>
              <DropdownItem onSelect={() => {}}>
                <DropdownItemContent>{t('actions.reject')}</DropdownItemContent>
              </DropdownItem>
            </Dropdown>
          </>
        )}
      </div>
    </div>
  );
}

// ─── List view (scholarship-tab endpoint) ────────────────────────────────────

function ScholarshipListView({ scholarships }: { scholarships: readonly ScholarshipDetail[] }) {
  const { t } = useTranslation('personManagement');

  return (
    <SectionCard
      header={
        <SectionTitle
          icon={AcademicCapIcon}
          title={t('enrollmentDetail.scholarshipDetails')}
          description={t('enrollmentScholarship.listDescription', {
            count: scholarships.length,
          })}
        />
      }
    >
      {scholarships.length === 0 ? (
        <p className="ts-paragraph-xs text-ctx-content-placeholder py-xxs">
          {t('enrollmentScholarship.noScholarships')}
        </p>
      ) : (
        <div className="flex flex-col">
          {scholarships.map((s, index) => (
            <div key={s.courseFinancialPersonId}>
              {index > 0 && <div className="border-t-hairline border-ctx-layout-border" />}
              <div className="py-xxxs">
                {/* Header row: name + status badge */}
                <div className="flex flex-wrap items-center justify-between gap-xs mb-xxxs">
                  <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
                    {s.courseFinancialDescription || '—'}
                  </span>
                  <Tag
                    label={
                      s.enrollmentStatus
                        ? t(`scholarshipEnrollmentStatus.${s.enrollmentStatus}`)
                        : t('scholarshipEnrollmentStatus.unknown')
                    }
                    size="sm"
                    status={
                      s.enrollmentStatus
                        ? SCHOLARSHIP_ENROLLMENT_STATUS_TAG_MAP[s.enrollmentStatus]
                        : 'default'
                    }
                    custom={{ text: 'text-nowrap' }}
                  />
                </div>

                {/* Info grid */}
                <div className="grid grid-cols-2 gap-xxxs md:grid-cols-4">
                  <InfoField
                    label={t('enrollmentDetail.scholarshipType')}
                    value={s.scholarshipTypeDescription || '—'}
                  />
                  <InfoField
                    label={t('enrollmentDetail.schoolDiscount')}
                    value={
                      s.schoolDiscount != null && s.schoolDiscount > 0
                        ? `${s.schoolDiscount}%`
                        : '—'
                    }
                  />
                  {s.scholarshipReason && (
                    <InfoField
                      label={t('enrollmentDetail.justification')}
                      value={s.scholarshipReason}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ─── Full view (scholarshipFull endpoint) ────────────────────────────────────

function ScholarshipFullView({
  scholarshipFull,
  onAlterStatus,
  onExtendDeadline,
}: {
  scholarshipFull: EnrollmentScholarshipFull;
  onAlterStatus?: () => void;
  onExtendDeadline?: () => void;
}) {
  const { t } = useTranslation('personManagement');

  const discountedMonthly =
    scholarshipFull.monthlyValue * (1 - scholarshipFull.schoolDiscount / 100);

  const activationEntry = scholarshipFull.history.find(h => h.newStatus === 'active');

  const validityDisplay = scholarshipFull.validUntil
    ? t('enrollmentScholarship.validityRange', {
        from: new Date(scholarshipFull.validFrom).toLocaleDateString('pt-BR'),
        until: new Date(scholarshipFull.validUntil).toLocaleDateString('pt-BR'),
      })
    : t('enrollmentScholarship.validityFrom', {
        from: new Date(scholarshipFull.validFrom).toLocaleDateString('pt-BR'),
      });

  return (
    <>
      {/* ── Banner de aprovação ── */}
      {scholarshipFull.activeStatus === 'active' && (
        <div className="flex items-center gap-xs rounded-sm border-hairline border-base-success-400 bg-base-success-100 px-xxs py-xxxs">
          <CheckCircleIcon className="h-xs w-xs shrink-0 text-base-success-500" />
          <div className="flex flex-col gap-quark">
            <span className="ts-paragraph-xs !font-semibold text-base-success-700">
              {t('enrollmentScholarship.approvalBanner', {
                discount: scholarshipFull.schoolDiscount,
              })}
            </span>
            {activationEntry && (
              <span className="ts-caption-xxs text-base-success-600">
                {t('enrollmentScholarship.approvalBannerSince', {
                  date: new Date(activationEntry.changedAt).toLocaleDateString('pt-BR'),
                })}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Configuração da bolsa ── */}
      <SectionCard
        header={
          <SectionTitle
            icon={AcademicCapIcon}
            title={t('enrollmentScholarship.sectionConfigTitle')}
            description={t('enrollmentScholarship.sectionConfigDescription')}
            action={
              <Button hierarchy="tertiary" size="sm" onClick={onAlterStatus}>
                {t('enrollmentScholarship.alterStatus')}
              </Button>
            }
          />
        }
      >
        <div className="grid grid-cols-2 gap-xxxs md:grid-cols-4">
          <InfoField
            label={t('enrollmentScholarship.fieldPlan')}
            value={scholarshipFull.planDescription}
          />
          <InfoField
            label={t('enrollmentScholarship.fieldType')}
            value={scholarshipFull.scholarshipType}
          />
          <div className="flex flex-col gap-quark">
            <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
              {t('enrollmentScholarship.fieldStatus')}
            </span>
            <Tag
              label={t(`enrollmentScholarship.activeStatus.${scholarshipFull.activeStatus}`)}
              size="sm"
              status={ACTIVE_STATUS_TAG_MAP[scholarshipFull.activeStatus]}
              custom={{ text: 'text-nowrap' }}
            />
          </div>
          <InfoField
            label={t('enrollmentScholarship.fieldDiscount')}
            value={t('enrollmentScholarship.discountValue', {
              percent: scholarshipFull.schoolDiscount,
              monthly: formatCurrency(discountedMonthly),
            })}
          />
          <InfoField label={t('enrollmentScholarship.fieldValidity')} value={validityDisplay} />
          <div className="flex flex-col gap-quark">
            <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
              {t('enrollmentScholarship.fieldAutoRenew')}
            </span>
            <div className="flex flex-col gap-quark">
              <Tag
                label={t(
                  `enrollmentScholarship.${scholarshipFull.autoRenew ? 'autoRenewYes' : 'autoRenewNo'}`,
                )}
                size="sm"
                status={scholarshipFull.autoRenew ? 'positive' : 'default'}
                custom={{ text: 'text-nowrap' }}
              />
              {scholarshipFull.autoRenewDescription && (
                <span className="ts-caption-xxs text-ctx-content-placeholder">
                  {scholarshipFull.autoRenewDescription}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-quark">
            <span className="ts-caption-xxs tracking-wider text-ctx-content-placeholder uppercase">
              {t('enrollmentScholarship.fieldDocDeadline')}
            </span>
            <div className="flex flex-col gap-quark">
              <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
                {scholarshipFull.docDeadline
                  ? new Date(scholarshipFull.docDeadline).toLocaleDateString('pt-BR')
                  : '—'}
              </span>
              {scholarshipFull.docDeadlineNote && (
                <span className="ts-caption-xxs text-ctx-content-placeholder">
                  {scholarshipFull.docDeadlineNote}
                </span>
              )}
            </div>
          </div>
          <InfoField
            label={t('enrollmentScholarship.fieldExtensions')}
            value={String(scholarshipFull.extensionsGranted)}
          />
        </div>
      </SectionCard>

      {/* ── Documentos da bolsa ── */}
      <SectionCard
        header={
          <SectionTitle
            icon={DocumentTextIcon}
            title={t('enrollmentScholarship.sectionDocsTitle')}
            description={t('enrollmentScholarship.sectionDocsDescription', {
              count: scholarshipFull.documents.length,
            })}
          />
        }
      >
        <div className="flex flex-col">
          {scholarshipFull.documents.map((doc, index) => (
            <div key={doc.id}>
              {index > 0 && <div className="border-t-hairline border-ctx-layout-border" />}
              <ScholarshipDocumentRow doc={doc} />
            </div>
          ))}
          {scholarshipFull.reanalysisNotice && (
            <div className="mt-xxs flex items-start justify-between gap-xs rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body px-xxs py-xxxs">
              <div className="flex flex-col gap-quark">
                <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
                  {scholarshipFull.reanalysisNotice.title}
                </span>
                <span className="ts-caption-xxs text-ctx-content-placeholder">
                  {scholarshipFull.reanalysisNotice.message}
                </span>
              </div>
              <div className="shrink-0">
                <Button hierarchy="secondary" size="sm" onClick={onExtendDeadline}>
                  {t('enrollmentScholarship.extendDeadline')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      {/* ── Histórico detalhado de status ── */}
      <SectionCard
        header={
          <SectionTitle
            icon={ClockIcon}
            title={t('enrollmentScholarship.sectionHistoryTitle')}
            description={t('enrollmentScholarship.sectionHistoryDescription')}
          />
        }
      >
        <div className="flex flex-col gap-xxs">
          {scholarshipFull.history.map(item => (
            <div key={item.id} className="flex gap-xxs">
              <div className="flex flex-col items-center pt-quark">
                <div
                  className={`h-xxxs w-xxxs shrink-0 border-thin rounded-circular ${ACTIVE_STATUS_DOT_MAP[item.newStatus]}`}
                />
                <div className="h-full w-[0.0625rem] bg-ctx-layout-border" />
              </div>
              <div className="flex flex-1 flex-col gap-quark pb-xxs">
                <div className="flex items-center gap-xxs">
                  <span className="ts-paragraph-xs !font-medium text-ctx-content-title">
                    {item.oldStatus
                      ? `${t(`enrollmentScholarship.activeStatus.${item.oldStatus}`)} → ${t(`enrollmentScholarship.activeStatus.${item.newStatus}`)}`
                      : t(`enrollmentScholarship.activeStatus.${item.newStatus}`)}
                  </span>
                  <span className="ts-caption-xxs text-ctx-content-placeholder ml-auto">
                    {new Date(item.changedAt).toLocaleString('pt-BR')}
                  </span>
                </div>
                <div className="ml-mini flex flex-col gap-quark rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs">
                  {item.action && (
                    <div className="flex gap-mini">
                      <span className="ts-caption-xxs !font-medium text-ctx-content-title shrink-0">
                        {t('enrollmentScholarship.historyAction')}:
                      </span>
                      <span className="ts-caption-xxs text-ctx-content-base">{item.action}</span>
                    </div>
                  )}
                  {item.planDescription && (
                    <div className="flex gap-mini">
                      <span className="ts-caption-xxs !font-medium text-ctx-content-title shrink-0">
                        {t('enrollmentScholarship.historyPlan')}:
                      </span>
                      <span className="ts-caption-xxs text-ctx-content-base">
                        {item.planDescription}
                      </span>
                    </div>
                  )}
                  {item.justification && (
                    <div className="flex gap-mini">
                      <span className="ts-caption-xxs !font-medium text-ctx-content-title shrink-0">
                        {t('enrollmentScholarship.historyJustification')}:
                      </span>
                      <span className="ts-caption-xxs text-ctx-content-base">
                        {item.justification}
                      </span>
                    </div>
                  )}
                  {item.observation && (
                    <div className="flex gap-mini">
                      <span className="ts-caption-xxs !font-medium text-ctx-content-title shrink-0">
                        {t('enrollmentScholarship.historyObservation')}:
                      </span>
                      <span className="ts-caption-xxs text-ctx-content-base">
                        {item.observation}
                      </span>
                    </div>
                  )}
                </div>
                <span className="ts-caption-xxs text-ctx-content-placeholder">
                  {t('enrollmentScholarship.historyBy', {
                    person: item.changedBy,
                    role: item.changedByRole,
                  })}
                </span>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function EnrollmentScholarshipTab({
  scholarships,
  scholarshipFull,
  onAlterStatus,
  onExtendDeadline,
}: EnrollmentScholarshipTabProps) {
  return (
    <div className="flex flex-col gap-xxs bg-ctx-layout-body py-xs">
      {scholarshipFull ? (
        <ScholarshipFullView
          scholarshipFull={scholarshipFull}
          {...(onAlterStatus !== undefined ? { onAlterStatus } : {})}
          {...(onExtendDeadline !== undefined ? { onExtendDeadline } : {})}
        />
      ) : (
        <ScholarshipListView scholarships={scholarships} />
      )}
    </div>
  );
}
